import { gql } from '@apollo/client';
import { useDisclosure } from '@dwarvesf/react-hooks';
import { Box, Typography } from '@mui/material';
import { IconBoldInfoCircleAlt } from 'components/icons/components/bold/IconBoldInfoCircleAlt';
import { useGetCreatorProfileBrandMentionStatsGraphForSlBrandMentionCardQuery } from 'graphql/generated';
import moment from 'moment';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';
import {
  BRAND_MENTION_MODAL_TYPES,
  SocialListeningBrandMentionDialog,
} from './SocialListeningBrandMentionDialog';

// eslint-disable-next-line
gql`
  query GetCreatorProfileBrandMentionStatsGraphForSLBrandMentionCard(
    $input: CreatorProfileBrandMentionStatsInput!
  ) {
    creatorProfileBrandMentionStatsGraph(input: $input) {
      totalCount
      previousDurationTotalCount
      data {
        date
        brandMentionCount
      }
    }
  }
`;

interface Props {
  brandId: string;
}

export const SocialListeningBrandMentionCard = ({ brandId }: Props) => {
  const brandMentionDialog = useDisclosure();

  const [searchParams] = useSearchParams();
  const modalType = searchParams.get('modalType');

  // show last 30 days data by default
  const { dateRange, timezone } = useMemo(
    () => ({
      dateRange: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
      },
      timezone: moment.tz.guess(),
    }),
    [],
  );
  const { data } =
    useGetCreatorProfileBrandMentionStatsGraphForSlBrandMentionCardQuery({
      variables: {
        input: {
          brandId,
          dateRange,
          timezone,
        },
      },
    });

  const brandMentionData = data?.creatorProfileBrandMentionStatsGraph || {
    data: [],
    totalCount: 0,
    previousDurationTotalCount: 0,
  };

  useEffect(() => {
    if (modalType && BRAND_MENTION_MODAL_TYPES.includes(modalType as any)) {
      brandMentionDialog.onOpen();
    }
  }, [modalType]); // eslint-disable-line

  return (
    <>
      <Box
        sx={{
          p: 4,
          borderRadius: 3,
          background: 'rgba(217, 227, 243, 0.50)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          cursor: 'pointer',
          flex: 1,
        }}
        onClick={brandMentionDialog.onOpen}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Typography
            variant="headline-xs"
            color={theme.colors?.utility['blue-4']}
          >
            Brand mentions (paid and organic)
          </Typography>
          <IconBoldInfoCircleAlt
            size={16}
            color={theme.colors?.utility['blue-4']}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="headline-xl"
            fontSize={24}
            fontWeight={500}
            letterSpacing="-0.48px"
          >
            {formatBigNumber(brandMentionData?.totalCount)} mentions
          </Typography>
          <Typography
            variant="headline-sm"
            color={theme.colors?.utility['green-3']}
          >
            ↑{formatBigNumber(brandMentionData.previousDurationTotalCount)}{' '}
            {brandMentionData.totalCount
              ? `(${(
                  (brandMentionData.previousDurationTotalCount /
                    brandMentionData.totalCount) *
                  100
                ).toFixed(2)}%) vs last month`
              : 'N/A'}
          </Typography>
        </Box>
        {brandMentionData.data.length > 0 ? (
          <ResponsiveContainer width="100%" height={130}>
            <LineChart
              data={brandMentionData.data}
              margin={{
                top: 10,
                right: 0,
                left: -20,
                bottom: 0,
              }}
            >
              <CartesianGrid
                vertical={false}
                horizontal
                stroke={theme.colors?.utility[400]}
                strokeDasharray="6 6"
              />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => moment(value).format('MMM YYYY')}
                axisLine={false}
                tickLine={false}
                stroke={theme.colors?.utility[600]}
                fontSize={theme.typography['headline-sm'].fontSize}
                fontWeight={theme.typography['headline-sm'].fontWeight}
                fontFamily={theme.typography['headline-sm'].fontFamily}
                ticks={[
                  brandMentionData.data[0].date,
                  brandMentionData.data[brandMentionData.data.length - 1].date,
                ]}
                padding={{ left: 30, right: 30 }}
              />
              <YAxis
                dataKey="brandMentionCount"
                tickFormatter={(value) => value}
                axisLine={false}
                tickLine={false}
                stroke={theme.colors?.utility[600]}
                fontSize={theme.typography['headline-sm'].fontSize}
                fontWeight={theme.typography['headline-sm'].fontWeight}
                fontFamily={theme.typography['headline-sm'].fontFamily}
              />
              <Line
                type="monotone"
                dataKey="brandMentionCount"
                stroke="#A0B5D5"
                strokeWidth={4}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Typography variant="subhead-sm" color={theme.colors?.utility[600]}>
            No mentions in the past 30 days
          </Typography>
        )}
      </Box>
      {brandMentionDialog.isOpen && (
        <SocialListeningBrandMentionDialog
          onClose={brandMentionDialog.onClose}
          brandId={brandId}
        />
      )}
    </>
  );
};
